import assign from 'object-assign';
import { EventEmitter } from 'events';
import ROLES from 'constants/UserRoles';
import USER_TYPES from 'constants/UserTypes';
import registerStore from '../utils/StoreUtils';
import AdminAppDispatcher from '../dispatcher/AdminAppDispatcher';
import AdminConstants from '../constants/AdminConstants';
import Session from './entities/session';

const session = new Session();

const SessionStore = assign({}, EventEmitter.prototype, {
  canAccessUnifiedView() {
    return this.isAdmin();
  },

  // Public API: Getters (used by controller-views)
  isLoggedIn() {
    return !!this.getToken();
  },

  isGodAdmin() {
    return this.getUserRoles().includes(ROLES.GOD);
  },

  isAdmin() {
    const user = this.getUser();
    return user && user._type === ROLES.ADMIN;
  },

  isConsignee() {
    const user = this.getUser();
    return user && user._type === 'Consignee';
  },

  isWarehouseUser() {
    const user = this.getUser();
    return user && user._type === USER_TYPES.WAREHOUSE;
  },

  isFinance() {
    const user = this.getUser();
    return user && this.hasFinanceRole();
  },

  isFinanceAdmin() {
    const user = this.getUser();
    const roles = this.getUserRoles();
    return (
      user &&
      (roles.includes(ROLES.FINANCE_ADMIN) ||
        (roles.includes(ROLES.GOD) && roles.includes(ROLES.FINANCE)))
    );
  },

  isBridgeCodeManager() {
    const user = this.getUser();
    const roles = this.getUserRoles();
    return (
      user &&
      (roles.includes(ROLES.BRIDGE_CODE_MANAGER) || roles.includes(ROLES.GOD))
    );
  },

  isAbleToEditShorthaulFSC() {
    const user = this.getUser();
    const roles = this.getUserRoles();
    return (
      user &&
      (roles.includes(ROLES.SHIPPER_LANE_PRICING_MANAGER) ||
        roles.includes(ROLES.GOD))
    );
  },

  isAbleToEditLaneRate() {
    const user = this.getUser();
    const roles = this.getUserRoles();
    return (
      user &&
      ((roles.includes(ROLES.SHIPPER_LANE_PRICING_MANAGER) &&
        roles.includes(ROLES.CARRIER_LANE_PRICING_MANAGER)) ||
        roles.includes(ROLES.BULK_PRICING_MANAGER) ||
        roles.includes(ROLES.CARRIER_BULK_AUDITOR) ||
        roles.includes(ROLES.GOD))
    );
  },

  isAbleToEditAccessorialLaneRate() {
    const user = this.getUser();
    const roles = this.getUserRoles();
    return (
      user &&
      (roles.includes(ROLES.SHIPPER_LANE_PRICING_MANAGER) ||
        roles.includes(ROLES.CARRIER_LANE_PRICING_MANAGER) ||
        roles.includes(ROLES.BULK_PRICING_MANAGER) ||
        roles.includes(ROLES.CARRIER_BULK_AUDITOR) ||
        roles.includes(ROLES.GOD))
    );
  },

  isAbleToUploadCSVLaneRates() {
    const user = this.getUser();
    const roles = this.getUserRoles();
    return (
      user &&
      (roles.includes(ROLES.BULK_PRICING_MANAGER) || roles.includes(ROLES.GOD))
    );
  },

  isAbleToManageUVReport(report) {
    const user = this.getUser();
    const roles = this.getUserRoles();
    const isMyReport = report?.creator_id === user?._id;
    return (
      user &&
      (isMyReport ||
        roles.includes(ROLES.UV_REPORT_MANAGER) ||
        roles.includes(ROLES.GOD))
    );
  },

  isAbleToAdjustCarrierBulkCost() {
    const user = this.getUser();
    const roles = this.getUserRoles();
    return (
      user &&
      (roles.includes(ROLES.CARRIER_BULK_AUDITOR) || roles.includes(ROLES.GOD))
    );
  },

  isAbleToAdjustDrayageFscConfig() {
    const user = this.getUser();
    const roles = this.getUserRoles();
    return (
      user &&
      (roles.includes(ROLES.SHIPPER_LANE_PRICING_MANAGER) ||
        roles.includes(ROLES.CARRIER_LANE_PRICING_MANAGER) ||
        roles.includes(ROLES.BULK_PRICING_MANAGER) ||
        roles.includes(ROLES.GOD))
    );
  },

  isThirdPartyCarrier() {
    const user = this.getUser();
    if (!user || !user.tags) {
      return false;
    }

    const { tags } = user;

    return (
      tags.includes('third_party_carrier') &&
      !tags.includes('marketplace_carrier')
    );
  },

  carrierHasAppointmentsAccess(user) {
    if (!user || !user.tags) {
      return false;
    }

    const { tags } = user;

    return (
      tags.includes('third_party_carrier') &&
      tags.includes('marketplace_carrier')
    );
  },

  isCustomerManagerAdmin() {
    const user = this.getUser();
    const roles = this.getUserRoles();
    return user && roles.includes(ROLES.CUSTOMER_MANAGER);
  },

  isQuotesOnly() {
    const user = this.getUser();
    return user && user.roles && user.roles.includes(ROLES.DRAYAGE_QUOTES_ONLY);
  },

  isShipper() {
    const user = this.getUser();
    return user && user._type === ROLES.SHIPPER;
  },

  isCarrier() {
    const user = this.getUser();
    return user && user._type === ROLES.CARRIER;
  },

  isMasterAccountUser() {
    const user = this.getUser();
    return user.roles.indexOf(ROLES.MASTER_ACCOUNT_USER) > -1;
  },

  isShipperLanePricingManager() {
    const user = this.getUser();
    return user.roles.includes(ROLES.SHIPPER_LANE_PRICING_MANAGER);
  },

  isCarrierLanePricingManager() {
    const user = this.getUser();
    return user.roles.includes(ROLES.CARRIER_LANE_PRICING_MANAGER);
  },

  getUser() {
    return session.get('user');
  },

  getMasterAccountId() {
    const user = this.getUser();
    const masterId = user?.master_account?.id;
    return masterId || user._id;
  },

  getRefreshToken() {
    return session.get('refresh-token');
  },

  getToken() {
    return session.get('token');
  },

  getUserRoles() {
    const user = this.getUser() || {};
    return user.roles || [];
  },

  hasFinanceAdminRole() {
    return this.hasRole('finance-admin');
  },

  hasShipperOnboardingRole() {
    return this.hasRole(ROLES.SHIPPER_ONBOARDING);
  },

  canShareFilters() {
    return this.hasRole('filter-creator');
  },
  hasIntegrationManagerRole() {
    return this.hasRole('integration_manager');
  },
  hasAccessorialManagerRole() {
    return this.hasRole('accessorial_manager');
  },
  hasTariffRole() {
    return this.hasRole('tariff');
  },

  hasAccountingAdminRole() {
    return this.hasRole('accounting-admin');
  },

  hasComplianceRole() {
    return this.hasRole('carrier-compliance');
  },

  hasFinanceRole() {
    return this.hasRole('finance');
  },

  hasAdminRole() {
    return this.hasRole('admin');
  },

  userAllowedToManageReferenceNumbersMasterList() {
    return (
      this.isGodAdmin() ||
      this.hasShipperOnboardingRole() ||
      this.hasIntegrationManagerRole()
    );
  },

  hasShipperLanePricingManagerRole() {
    return (
      this.hasRole(ROLES.SHIPPER_LANE_PRICING_MANAGER) ||
      this.hasRole(ROLES.BULK_PRICING_MANAGER)
    );
  },

  hasCarrierLanePricingManagerRole() {
    return (
      this.hasRole(ROLES.CARRIER_LANE_PRICING_MANAGER) ||
      this.hasRole(ROLES.BULK_PRICING_MANAGER)
    );
  },

  hasTripPlannerRole() {
    return this.hasRole(ROLES.TRIP_PLANNER_ADMIN);
  },

  hasOrderTemplateAdminRole() {
    return this.hasRole(ROLES.ORDER_TEMPLATE_ADMIN);
  },

  hasMultiStopBookingRole() {
    return this.hasRole(ROLES.MULTI_STOP_BOOKING);
  },

  hasRole(role) {
    const user = this.getUser();
    if (!user) return false;
    return user.roles.includes(role);
  },

  isACIAdmin() {
    return this.hasRole(ROLES.ACI_ZONES_ADMIN);
  },

  isMessageUser() {
    const messageUserRoles = [
      ROLES.MESSAGE_ONLY_USER,
      ROLES.MESSAGE_ADMIN,
      ROLES.MESSAGE_MANAGER,
      ROLES.MESSAGE_USER,
    ];
    const userRoles = this.getUserRoles();
    let messageUser = false;

    for (let i = 0; i < messageUserRoles.length; i += 1) {
      if (userRoles && userRoles.indexOf(messageUserRoles[i]) > -1) {
        messageUser = true;
        break;
      }
    }
    return messageUser;
  },

  isMessageOnlyUser() {
    return this.hasRole(ROLES.MESSAGE_ONLY_USER);
  },

  isMessagingAdmin() {
    return this.hasRole(ROLES.MESSAGE_ADMIN);
  },

  isMessagingManager() {
    return this.hasRole(ROLES.MESSAGE_MANAGER);
  },

  isGroupMessagingUser() {
    return this.hasRole(ROLES.GROUP_MESSAGE_USER);
  },

  isSupport() {
    return this.hasRole(ROLES.SUPPORT);
  },

  storeAccessAccountRequest(id, requestList) {
    return session.storeAccessAccountRequest(id, requestList);
  },

  getAccessAccountRequest(id) {
    return session.getAccessAccountRequest(id);
  },

  isAccessorialsAdmin() {
    return this.hasRole(ROLES.ACCESSORIALS_ADMIN);
  },

  getUserType() {
    const user = this.getUser() || {};
    return user._type;
  },

  getEquipmentUserColumnPreferences() {
    return session.getEquipmentUserColumnPreferences();
  },

  setEquipmentUserColumnPreferences(columns) {
    return session.setEquipmentUserColumnPreferences(columns);
  },

  getRedirectPath() {
    const path = session.get('redirectPath');
    session.unset('redirectPath', { silent: true });
    return path;
  },

  setRedirectPath(path) {
    session.set('redirectPath', path);
  },

  getError() {
    return session.error();
  },
  canGenerateCreditMemo() {
    return (
      this.hasFinanceRole() || this.isFinanceAdmin() || this.hasAdminRole()
    );
  },

  isReferenceNumbersAdmin() {
    return this.hasRole(ROLES.REFERENCE_NUMBERS_ADMIN);
  },

  isBillingManagerAdmin() {
    return this.hasRole(ROLES.BILLING_MANAGER_ADMIN);
  },

  isShipmentClosureAdmin() {
    // don't want to add this to ROLES (secret role)
    return this.hasRole('shipment-closure-admin');
  },

  isBillingStatusAdmin() {
    return this.hasRole(ROLES.BILLING_STATUS_ADMIN);
  },

  isBillingStatusManager() {
    return this.hasRole(ROLES.BILLING_STATUS_MANAGER);
  },

  isBillingStatusUser() {
    return this.hasRole(ROLES.BILLING_STATUS_USER);
  },

  isUvReportManager() {
    return this.hasRole(ROLES.UV_REPORT_MANAGER);
  },

  // Public API: Handler Registration

  addChangeListener(callback) {
    session.on('change', callback);
    this.on('change', callback);
  },

  removeChangeListener(callback) {
    session.off('change', callback);
    this.removeListener('change', callback);
  },

  emitChange() {
    this.emit('change');
  },
  // Event Handlers for Dispatcher (unpack action and call Public API)

  onLogout() {
    session.logout();
    this.emitChange();
  },

  onLoginOk(action) {
    session.login(action.token, action.user);
    this.emitChange();
  },

  onLoginFailed(action) {
    session.logout();
    session.error(action.message);
  },

  onAuthCheckFailed() {
    session.logout();
  },

  onPasswordResetOk(action) {
    session.logout();
    session.error(action.message);
  },

  onPasswordResetFailed(action) {
    session.logout();
    session.error(action.message);
  },

  onPasswordChangeOk(action) {
    session.logout();
    session.error(action.message);
  },

  onPasswordChangeFailed(action) {
    session.logout();
    session.error(action.message);
  },

  onCarrierUpdated(payload) {
    const user = this.getUser();

    const carrier = payload.carrier;
    if (user._type === 'Carrier' && carrier) {
      session.storeUser(carrier);
      session.trigger('change');
    }
  },

  onAuthCheckSuccess(action) {
    const user = action.response.user;

    if (!user || !this.getUser()) {
      session.clear();
      session.logout();
      return;
    }

    if (user._type === 'Admin') {
      session.storeUser(user);
      session.trigger('change');
    }

    if (user._type === 'Carrier') {
      const carrier = this.getUser();
      // Set new auth permissions on page refresh
      carrier.authorized = user.authorized;
      carrier.date_authorized = user.date_authorized;
      session.storeUser(carrier);
      session.trigger('change');
    }
  },

  onUserUpdated(payload) {
    const user = this.getUser();
    if (user._type !== 'Admin') {
      session.storeUser(payload.user);
      session.trigger('change');
    }
  },

  onNotificationPreferencesUpdateFailed(payload) {
    const user = this.getUser();
    if (user?._type !== 'Admin') {
      session.storeUser({
        ...user,
      });
      session.trigger('change');
    }
  },

  onNotificationPreferencesUpdate(payload) {
    const user = this.getUser();
    if (user._type !== 'Admin') {
      const { notification_preferences } = payload.user;
      session.storeUser({
        ...user,
        notification_preferences,
      });
      session.trigger('change');
    }
  },

  onShipperUpdated(payload) {
    const user = session.get('user');

    let shipper = payload.shipper;
    if (user._type === 'Shipper' && shipper) {
      shipper = {
        ...user,
        ...shipper,
      };
      session.storeUser(shipper);
      session.trigger('change');
    }
  },

  onVendorUpdated(payload) {
    const user = session.get('user');

    let vendor = payload.vendor;
    if (user._type === 'Vendor' && vendor) {
      vendor = {
        ...user,
        ...vendor,
      };
      session.storeUser(vendor);
      session.trigger('change');
    }
  },

  onShipperCreditCardUpdated(data) {
    const user = session.get('user');

    if (user._type === 'Shipper' && data.stripe_customer_id) {
      user.stripe_customer_id = data.stripe_customer_id;
      session.storeUser(user);
      session.trigger('change');
    }
  },

  storeUser(user) {
    session.storeUser(user);
  },

  setLoginAs(user) {
    session.setLoginAs(user);
    session.trigger('change');
  },

  setCarrierPartner(partnerId) {
    session.setCarrierPartner(partnerId);
    session.trigger('change');
  },

  getShipperWarehouseRelationStatus() {
    return session.getShipperWarehouseRelationStatus();
  },

  setShipperWarehouseRelationStatus(status) {
    session.setShipperWarehouseRelationStatus(status);
    session.trigger('change');
  },

  getCarrierPartner() {
    return session.getCarrierPartner();
  },

  getLoginAs() {
    return session.getLoginAs();
  },

  setAuthToken(token) {
    session.storeToken(token);
  },

  setLanguagePreference(id, languageCode) {
    return session.setLocalePreference(id, languageCode);
  },

  getLanguagePreference(id) {
    return session.getLocalePreference(id);
  },

  setLanguageCodeIdPreference(id, languageCodeId) {
    return session.setLanguageCodeIdPreference(id, languageCodeId);
  },

  getLanguageCodeIdPreference(id) {
    return session.getLanguageCodeIdPreference(id);
  },
});

// Handlers for dispatched events

const ActionTypes = AdminConstants.ActionTypes;
const handlers = {
  [ActionTypes.LOGOUT]: 'onLogout',
  [ActionTypes.LOGIN_SUCCESS]: 'onLoginOk',
  [ActionTypes.LOGIN_FAIL]: 'onLoginFailed',
  [ActionTypes.AUTH_CHECK_FAILED]: 'onAuthCheckFailed',
  [ActionTypes.PASSWORD_RESET_REQUEST_SUCCESS]: 'onPasswordResetOk',
  [ActionTypes.PASSWORD_RESET_REQUEST_FAILURE]: 'onPasswordResetFailed',
  [ActionTypes.PASSWORD_CHANGE_SUCCESS]: 'onPasswordChangeOk',
  [ActionTypes.PASSWORD_CHANGE_FAILURE]: 'onPasswordChangeFailed',
  [ActionTypes.UPDATED_CARRIER]: 'onCarrierUpdated',
  [ActionTypes.UPDATE_CARRIER_DOCUMENTS_SUCCESS]: 'onCarrierUpdated',
  [ActionTypes.UPDATE_NOTIFICATIONS_SUCCESS]: 'onNotificationPreferencesUpdate',
  [ActionTypes.UPDATE_NOTIFICATIONS_FAIL]:
    'onNotificationPreferencesUpdateFailed',
  [ActionTypes.UPDATED_SHIPPER]: 'onShipperUpdated',
  [ActionTypes.UPDATED_VENDOR]: 'onVendorUpdated',
  [ActionTypes.UPDATED_SHIPPER_BILLING]: 'onShipperCreditCardUpdated',
  [ActionTypes.AUTH_CHECK_SUCCESS]: 'onAuthCheckSuccess',
};

registerStore(SessionStore, AdminAppDispatcher, handlers);

export default SessionStore;
